import { ReactNode } from 'react';
import clsx from 'clsx';
import Image from 'next/image';
import { cva } from 'class-variance-authority';
import { Dialog, Transition } from '@headlessui/react';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';

import { CloseButton } from '@/components/atoms/CloseButton';
import { Wrapper } from '@/components/atoms/Wrapper';

export interface HeadlessModalProps {
  variant: 'default' | 'authModal';
  isArrowLeft?: boolean;
  children: ReactNode;
  className?: string;
  containerClassName?: string;
  isOpen: boolean;
  title?: string;
  isLogo?: boolean;
  onArrowLeftClick?: () => void;
  closeModal: () => void | ((value: boolean) => void);
}

const modalVariants = cva('', {
  variants: {
    variant: {
      default:
        'dark:text-white relative text-black border dark:border-gray-800 border-gray-250 rounded-lg overflow-y-auto dark:bg-black bg-white_theme-50 p-4 m-4',
      authModal:
        'dark:text-white text-black sm:border dark:sm:border-gray-800 border-gray-250 sm:rounded-lg h-full sm:max-h-[90vh] px-2 sm:px-4 pt-6 pb-6 overflow-y-auto dark:bg-black bg-white_theme-50',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export const HeadlessModal = ({
  children,
  isOpen,
  variant,
  title = '',
  className,
  containerClassName,
  isLogo = true,
  isArrowLeft = false,
  closeModal,
  onArrowLeftClick,
}: HeadlessModalProps) => {
  return (
    <Transition appear show={isOpen}>
      <Dialog as="div" className="relative z-50" onClose={closeModal}>
        <Transition.Child
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-75" />
        </Transition.Child>

        <div className={clsx('overflow-y-auto fixed inset-0 w-screen', containerClassName)}>
          <div
            className={clsx(
              'flex items-center justify-center',
              variant === 'default' ? '!h-auto !min-h-screen' : '!h-full sm:!h-auto sm:!min-h-screen',
            )}
          >
            <Transition.Child
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              className={clsx('w-full h-full sm:my-auto', className)}
            >
              <Dialog.Panel className={modalVariants({ variant })}>
                <Dialog.Title className="flex justify-between items-center pb-5 pl-2">
                  <Wrapper row>
                    {isArrowLeft && (
                      <button onClick={onArrowLeftClick}>
                        <ArrowLeftIcon
                          className="hover:opacity-80 dark:text-white text-black stroke-2 mr-3"
                          width={24}
                          height={24}
                        />
                      </button>
                    )}
                    {!!title && <h2 className="text-base font-bold dark:text-white text-black sm:text-xl">{title}</h2>}
                    {!title && isLogo && (
                      <Image
                        src="/icons/fangol-logo.svg"
                        alt="Fangol logo"
                        width={91}
                        height={28}
                        priority
                        className="dark:invert-0 invert"
                      />
                    )}
                  </Wrapper>
                  <CloseButton variant="default" onClick={closeModal} />
                </Dialog.Title>
                  {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
